import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Skeleton,
  Alert,
  Typography,
  Box,
  TableSortLabel,
  Collapse,
  Tooltip,
  Chip
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  KeyboardArrowDown as ChevronDownIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  AnalyticsTableProps,
  AnalyticsDataPoint,
  TableColumn,
  HierarchyNode
} from '../types/analytics.types';
import { UnifiedFilterComponent } from './UnifiedFilterComponent';
import { getColumnTooltip, hasColumnTooltip } from '../utils/column-tooltips';

// Styled components - Light theme matching Figma
const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e4e4e7',
  borderRadius: '8px',
  boxShadow: 'none',
}));

const StyledTableCell = styled(TableCell)<{ level?: number }>(({ theme, level = 0 }) => ({
  paddingLeft: level === 0 ? '16px' : `${16 + level * 12}px`,
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
  borderBottom: '1px solid #e4e4e7',
  fontFamily: "'Inter', sans-serif",
  fontWeight: level === 0 ? 500 : 400,
  fontSize: '14px',
  lineHeight: '20px',
  backgroundColor: '#ffffff',
  color: '#18181b',
  height: '64px',
  verticalAlign: 'middle',
}));

const ExpandableCell = styled(TableCell)<{ level?: number }>(({ theme, level = 0 }) => ({
  paddingLeft: level === 0 ? '16px' : `${16 + level * 12}px`,
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderBottom: '1px solid #e4e4e7',
  fontFamily: "'Inter', sans-serif",
  fontWeight: level === 0 ? 500 : 400,
  fontSize: '14px',
  lineHeight: '20px',
  backgroundColor: '#ffffff',
  color: '#18181b',
  height: '64px',
  verticalAlign: 'middle',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#ffffff',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  overflow: 'visible',
  '& .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    overflow: 'visible',
  },
  '& .MuiTableCell-root': {
    backgroundColor: '#ffffff',
    color: '#71717a',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    borderBottom: '1px solid #e4e4e7',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '16px',
    paddingRight: '16px',
    height: '56px',
    verticalAlign: 'middle',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid #e4e4e7',
  borderRadius: '8px',
  maxWidth: '100%',
  overflowX: 'hidden',
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    height: '12px',
    width: '12px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f4f4f5',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#a1a1aa',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#71717a',
    },
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: '#f4f4f5',
  },
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    boxShadow: 'none',
  },
  '& .MuiTable-root': {
    backgroundColor: '#ffffff',
    minWidth: 650,
    overflow: 'visible',
  },
  '& .MuiTableBody-root': {
    backgroundColor: '#ffffff',
  },
  '& .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const DividerCell = styled(TableCell)(({ theme }) => ({
  padding: '0 !important',
  paddingTop: '0 !important',
  paddingBottom: '0 !important',
  paddingLeft: '0 !important',
  paddingRight: '0 !important',
  width: '4px',
  backgroundColor: '#ffffff',
  border: 'none !important',
  borderBottom: 'none !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
  position: 'relative',
  verticalAlign: 'middle',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: '#d4d4d8',
    transform: 'translateX(-50%)',
  },
}));

const DividerHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: '0 !important',
  paddingTop: '0 !important',
  paddingBottom: '0 !important',
  paddingLeft: '0 !important',
  paddingRight: '0 !important',
  width: '4px',
  backgroundColor: '#ffffff',
  border: 'none !important',
  borderBottom: 'none !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  verticalAlign: 'middle',
  boxShadow: 'none !important',
  height: '56px',
  overflow: 'visible !important',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '2px',
    backgroundColor: '#d4d4d8',
    transform: 'translateX(-50%)',
  },
}));

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface AnalyticsTableRef {
  expandAll: () => void;
  collapseAll: () => void;
}

export const AnalyticsTable = forwardRef<AnalyticsTableRef, AnalyticsTableProps>(({
  data,
  config,
  hierarchy,
  onExpandNode,
  onCollapseNode,
  loading = false,
  error,
  className,
  highlightDeviations = false,
  calculateDeviation,
  varianceDisplayMode = 'absolute',
  showCreditCardDetail = false,
  showThirdPartyDetail = false,
  showAllPaymentDetails = false,
  onCreditCardDetailChange,
  onThirdPartyDetailChange
}, ref) => {

  // Initialize table data processing
  // ALL HOOKS MUST BE CALLED FIRST - before any conditional logic or early returns
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    config?.columnFilter?.defaultSelection || []
  );
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());

  // Sync expandedColumns with showCreditCardDetail, showThirdPartyDetail, and showAllPaymentDetails props
  React.useEffect(() => {
    setExpandedColumns(prevExpanded => {
      const newExpanded = new Set(prevExpanded);

      // Find all expandable payment columns (have revealsColumns)
      const expandableColumns = config?.columns?.filter(col => col.revealsColumns && col.revealsColumns.length > 0) ?? [];

      expandableColumns.forEach(col => {
        const isCreditCard = col.id === 'credit_cards_total' || col.id === 'credit_card_payments';
        const isThirdParty = col.id === 'third_party_delivery';

        const shouldExpand = showAllPaymentDetails ||
          (isCreditCard && showCreditCardDetail) ||
          (isThirdParty && showThirdPartyDetail);

        if (shouldExpand) {
          newExpanded.add(col.id);
        } else {
          newExpanded.delete(col.id);
        }
      });

      return newExpanded;
    });
  }, [showCreditCardDetail, showThirdPartyDetail, showAllPaymentDetails, config?.columns]);

  // Initialize expansion state from data (only on first load, preserve user expansions)
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);

  // Auto-scaling to fit all columns
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [tableScale, setTableScale] = React.useState<number>(1);

  React.useEffect(() => {
    if (data && data.length > 0 && config?.hierarchical) {
      if (!hasInitializedExpansion) {
        // First load: expand all drawers by default
        const initialExpanded = new Set<string>();
        data.forEach(row => {
          if (row.hasChildren) {
            const nodeKey = row.id || row.location || '';
            if (nodeKey) {
              initialExpanded.add(nodeKey);
            }
          }
        });
        setExpandedNodes(initialExpanded);
        setHasInitializedExpansion(true);
      } else {
        // Subsequent loads (filtering): preserve existing expansions for nodes that still exist
        setExpandedNodes(prevExpanded => {
          const validNodeKeys = new Set<string>();
          data.forEach(row => {
            if (row.hasChildren) {
              const nodeKey = row.id || row.location || '';
              validNodeKeys.add(nodeKey);
            }
          });

          // Keep only expanded nodes that still exist in the current data
          const preservedExpanded = new Set<string>();
          prevExpanded.forEach(nodeKey => {
            if (validNodeKeys.has(nodeKey)) {
              preservedExpanded.add(nodeKey);
            }
          });

          return preservedExpanded;
        });
      }
    }
  }, [data, config?.hierarchical, hasInitializedExpansion]);

  // Initialize visible columns from config
  React.useEffect(() => {
    if (config?.columnFilter?.enabled && config.columnFilter.defaultSelection) {
      setVisibleColumns(config.columnFilter.defaultSelection);
    }
  }, [config?.columnFilter]);

  // Calculate and apply auto-scaling to fit all columns
  React.useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !config?.columns) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth;

      // Calculate filtered columns inline (same logic as getFilteredColumns)
      let filteredCols: TableColumn[] = [];
      if (!config.columnFilter?.enabled) {
        filteredCols = config.columns;
      } else {
        filteredCols = config.columns.filter(column => {
          if (!column.filterable) return true;
          return visibleColumns.includes(column.field);
        });
      }

      // Filter columns based on expansion state
      filteredCols = filteredCols.filter(column => {
        // Always show divider columns
        if (column.dataType === 'divider' || column.type === 'divider') return true;

        // If column is revealed by a parent column, only show if parent is expanded
        if (column.revealedBy) {
          return expandedColumns.has(column.revealedBy);
        }

        // Otherwise, show the column
        return true;
      });

      // Calculate total width of all visible columns
      let totalTableWidth = 0;

      filteredCols.forEach(column => {
        // Parse width - could be number (px) or string (e.g., "200px", "20%")
        const width = column.width;
        if (typeof width === 'number') {
          totalTableWidth += width;
        } else if (typeof width === 'string') {
          const widthStr: string = width;
          const numValue = parseFloat(widthStr);
          if (widthStr.includes('%')) {
            // Percentage width - estimate based on container
            totalTableWidth += (containerWidth * numValue) / 100;
          } else if (widthStr.includes('px')) {
            totalTableWidth += numValue;
          } else {
            // Fallback: assume pixels
            totalTableWidth += numValue || 150; // Default column width
          }
        } else {
          // No width specified - use default
          totalTableWidth += 150;
        }
      });

      // Add some padding for borders/spacing
      totalTableWidth += 32;

      // Calculate scale to fit all columns
      if (totalTableWidth > 0 && containerWidth > 0) {
        const scale = Math.min(1, containerWidth / totalTableWidth);
        setTableScale(scale);
      } else {
        setTableScale(1);
      }
    };

    // Small delay to ensure container is rendered
    const timeoutId = setTimeout(() => {
      calculateScale();
    }, 0);

    // Recalculate on window resize
    const handleResize = () => {
      calculateScale();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [config?.columns, config?.columnFilter?.enabled, visibleColumns, data, expandedColumns]);

  // Memoize percentage columns to avoid dependency issues
  const percentageColumns = React.useMemo(() => {
    if (!config?.columns) return [];
    return config.columns.filter(
      col => col.dataType === 'percentage' && col.calculated
    );
  }, [config?.columns]);

  // Helper function for hierarchical sorting - sorts each level relative to its parent
  const sortHierarchically = React.useCallback((
    rows: AnalyticsDataPoint[],
    sortConfig: SortConfig
  ): AnalyticsDataPoint[] => {
    // Build a map of parent -> children
    const childrenMap = new Map<string, AnalyticsDataPoint[]>();
    const rootRows: AnalyticsDataPoint[] = [];

    rows.forEach(row => {
      if (row.level === 0 || !row.parentId) {
        // Top-level row (enterprise)
        rootRows.push(row);
      } else {
        // Child row - add to parent's children
        const parentId = row.parentId;
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        childrenMap.get(parentId)!.push(row);
      }
    });

    // Sort comparison function
    const compareValues = (a: AnalyticsDataPoint, b: AnalyticsDataPoint): number => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    };

    // Recursively sort and flatten the tree
    const sortAndFlatten = (parentRows: AnalyticsDataPoint[]): AnalyticsDataPoint[] => {
      // Sort the current level
      const sorted = [...parentRows].sort(compareValues);
      const result: AnalyticsDataPoint[] = [];

      sorted.forEach(parent => {
        // Add the parent
        result.push(parent);

        // Get and sort children
        const parentId = parent.id || '';
        const children = childrenMap.get(parentId) || [];

        if (children.length > 0) {
          // Sort children relative to this parent
          const sortedChildren = sortAndFlatten(children);
          result.push(...sortedChildren);
        }
      });

      return result;
    };

    // Start sorting from root level
    return sortAndFlatten(rootRows);
  }, []);

  // Sort data - this useMemo must also be called consistently
  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    // For hierarchical tables, wait for expansion initialization before processing
    if (config?.hierarchical && !hasInitializedExpansion) {
      return [];
    }

    const getVisibleRows = (allRows: AnalyticsDataPoint[]): AnalyticsDataPoint[] => {
      if (!config?.hierarchical) return allRows;

      const visibleRows: AnalyticsDataPoint[] = [];

      const processRow = (row: AnalyticsDataPoint) => {
        // Always show root level rows (level 0)
        if (row.level === 0) {
          visibleRows.push(row);
          return;
        }

        // For child rows, check if all parents are expanded
        let shouldShow = true;
        let currentParentId = row.parentId;

        while (currentParentId) {
          const parent = allRows.find(r => r.id === currentParentId);
          if (!parent) {
            break;
          }

          const parentKey = parent.id || parent.location || '';
          if (!expandedNodes.has(parentKey)) {
            shouldShow = false;
            break;
          }
          currentParentId = parent.parentId;
        }

        if (shouldShow) {
          visibleRows.push(row);
        }
      };

      allRows.forEach(processRow);
      return visibleRows;
    };

    // Get visible rows (either all rows for non-hierarchical, or filtered rows for hierarchical)
    const visibleRows = getVisibleRows(data);

    // STEP 1: Calculate dynamic percentages FIRST (before sorting)
    // This ensures percentage values exist when sorting by percentage columns
    let result = visibleRows;

    // Use memoized percentage columns
    if (percentageColumns.length > 0) {
      percentageColumns.forEach(column => {
        // Get formula - can be a string or ColumnFormulaConfig object
        let formulaString: string | undefined;
        if (typeof column.formula === 'string') {
          formulaString = column.formula;
        } else if (column.formula && typeof column.formula === 'object' && 'expression' in column.formula) {
          formulaString = column.formula.expression;
        }

        if (!formulaString) return;

        // Parse formula to extract base field (e.g., "net_sales" from "(net_sales / total_enterprise_sales) * 100")
        // Handle patterns like: "(net_sales / total_enterprise_sales) * 100" or "net_sales / total * 100"
        const formulaMatch = formulaString.match(/\(?\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\/\s*[^)]+\)?/);
        const baseField = formulaMatch ? formulaMatch[1].trim() : null;

        if (!baseField) {
          console.warn(`Could not parse base field from formula: ${formulaString}`);
          return;
        }

        // Calculate percentages relative to parent level for hierarchical tables
        if (config.hierarchical) {
          // Build a map for quick parent lookup
          const rowMap = new Map<string, AnalyticsDataPoint>();
          result.forEach(row => {
            const rowId = row.id || '';
            if (rowId) {
              rowMap.set(rowId, row);
            }
          });

          // Create new array with calculated percentages relative to parent
          result = result.map(row => {
            const value = Number(row[baseField]) || 0;
            let percentage = 0;
            const rowLevel = row.level || 0;

            if (rowLevel === 0) {
              // Top level (Enterprise): percentage relative to sum of all enterprises
              // If only one enterprise, it's 100%
              const allEnterprises = result.filter(r => (r.level || 0) === 0);
              if (allEnterprises.length === 1) {
                percentage = 100;
              } else {
                const enterpriseTotal = allEnterprises.reduce((sum, ent) => {
                  return sum + (Number(ent[baseField]) || 0);
                }, 0);
                if (enterpriseTotal > 0) {
                  percentage = (value / enterpriseTotal) * 100;
                }
              }
            } else if (row.parentId) {
              // Child level: percentage relative to parent
              const parent = rowMap.get(row.parentId);
              if (parent) {
                const parentValue = Number(parent[baseField]) || 0;
                if (parentValue > 0) {
                  percentage = (value / parentValue) * 100;
                }
              }
            }

            // Return new object with calculated percentage
            return {
              ...row,
              [column.field]: percentage
            };
          });
        } else {
          // Non-hierarchical: calculate percentage relative to total of all rows
          const total = result.reduce((sum, row) => {
            const value = Number(row[baseField]) || 0;
            return sum + value;
          }, 0);

          // Create new array with calculated percentages (immutable update)
          result = result.map(row => {
            const value = Number(row[baseField]) || 0;
            let percentage = 0;

            if (total > 0) {
              percentage = (value / total) * 100;
            }

            // Return new object with calculated percentage
            return {
              ...row,
              [column.field]: percentage
            };
          });
        }
      });
    }

    // STEP 2: Apply sorting AFTER percentages are calculated
    // Now percentage columns will have values to sort by
    if (!sortConfig) {
      return result;
    }

    // For hierarchical tables, sort relative to parent level
    if (config?.hierarchical) {
      return sortHierarchically(result, sortConfig);
    }

    // For non-hierarchical tables, use simple flat sort
    return [...result].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [data, sortConfig, expandedNodes, config?.hierarchical, hasInitializedExpansion, percentageColumns]);

  // Get filtered columns for rendering
  const getFilteredColumns = (): TableColumn[] => {
    if (!config || !config.columns) return [];

    let columns = config.columns;

    // If there's no columnFilter config, show all columns (assume external filtering like SmartDataManager)
    if (config.columnFilter?.enabled) {
      // Apply internal column filtering only if columnFilter is enabled
      columns = columns.filter(column => {
        // Always show divider columns
        if (column.dataType === 'divider' || column.type === 'divider') return true;
        // Always show non-filterable columns
        if (!column.filterable) return true;
        // Show filterable columns only if they're in the visible columns list
        return visibleColumns.includes(column.field);
      });
    }

    // Filter columns based on expansion state
    return columns.filter(column => {
      // Always show divider columns
      if (column.dataType === 'divider' || column.type === 'divider') return true;

      // If column is revealed by a parent column, only show if parent is expanded
      if (column.revealedBy) {
        return expandedColumns.has(column.revealedBy);
      }

      // Otherwise, show the column
      return true;
    });
  };

  const filteredColumns = getFilteredColumns();

  // Expose expandAll and collapseAll functions via ref
  // MUST be called before any early returns to maintain hook order
  useImperativeHandle(ref, () => ({
    expandAll: () => {
      if (!config?.hierarchical) return;

      const allExpandableNodes = new Set<string>();

      // Use the raw data to find all expandable nodes
      data.forEach(row => {
        if (row.hasChildren) {
          const nodeKey = row.id || row.location || '';
          if (nodeKey) {
            allExpandableNodes.add(nodeKey);
            if (onExpandNode) {
              onExpandNode(nodeKey);
            }
          }
        }
      });
      setExpandedNodes(allExpandableNodes);
    },
    collapseAll: () => {
      if (!config?.hierarchical) return;

      const currentExpanded = new Set(expandedNodes);
      setExpandedNodes(new Set());
      // Notify parent of all collapsed nodes
      if (onCollapseNode) {
        currentExpanded.forEach(nodeKey => {
          onCollapseNode(nodeKey);
        });
      }
    }
  }), [data, config?.hierarchical, onExpandNode, onCollapseNode, expandedNodes]);

  // NOW we can do conditional logic and early returns
  // Early validation to prevent render errors
  if (!config) {
    console.error('AnalyticsTable: config prop is required');
    return (
      <Alert severity="error">
        Table configuration is missing
      </Alert>
    );
  }

  if (!config.columns || !Array.isArray(config.columns)) {
    console.error('AnalyticsTable: config.columns is required and must be an array');
    return (
      <Alert severity="error">
        Table columns configuration is missing or invalid
      </Alert>
    );
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Box sx={{ width: '100%', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={200} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton variant="text" width="20%" height={24} />
            <Skeleton variant="text" width="10%" height={24} />
          </Box>
        </Box>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="warning">
        No data available
      </Alert>
    );
  }

  // Render column filter
  const renderColumnFilter = () => {
    if (!config.columnFilter?.enabled) return null;

    const filterOptions = config.columnFilter.options || [];

    return (
      <Box sx={{ mb: 2 }}>
        <UnifiedFilterComponent
          title={config.columnFilter.title}
          options={filterOptions}
          defaultValue={visibleColumns}
          placeholder="Select columns to display..."
          onChange={(newValues) => {
            setVisibleColumns(newValues);
          }}
          disabled={loading}
          allowSelectAll={config.columnFilter.allowSelectAll !== false}
        />
      </Box>
    );
  };

  // Filter data based on expansion state (function moved to useMemo hook)

  const handleSort = (field: string) => {
    if (!config.sortable) return;

    // Cycle through: no sort -> asc -> desc -> no sort (reset)
    if (!sortConfig || sortConfig.field !== field) {
      // New column or no sort: start with ascending
      setSortConfig({ field, direction: 'asc' });
    } else if (sortConfig.direction === 'asc') {
      // Currently ascending: switch to descending
      setSortConfig({ field, direction: 'desc' });
    } else {
      // Currently descending: reset to no sort
      setSortConfig(null);
    }
  };

  // Handle expand/collapse
  const handleToggleExpand = (row: AnalyticsDataPoint) => {
    if (!row.hasChildren) return;

    const nodeKey = row.id || row.location || '';
    const newExpanded = new Set(expandedNodes);

    if (expandedNodes.has(nodeKey)) {
      newExpanded.delete(nodeKey);
      if (onCollapseNode) {
        onCollapseNode(nodeKey);
      }
    } else {
      newExpanded.add(nodeKey);
      if (onExpandNode) {
        onExpandNode(nodeKey);
      }
    }
    setExpandedNodes(newExpanded);
  };

  // Helper function to get column alignment (default right for numeric types)
  const getColumnAlignment = (column: TableColumn): 'left' | 'center' | 'right' => {
    if (column.align) return column.align;
    // Default to right alignment for numeric data types
    if (column.dataType === 'currency' || column.dataType === 'percentage' || column.dataType === 'number') {
      return 'right';
    }
    return 'left';
  };

  // Format cell value
  const formatCellValue = (value: any, column: TableColumn): React.ReactNode => {
    if (value == null) return '-';

    switch (column.dataType) {
      case 'currency':
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) return value;

        // If column should always display in parentheses (for reductions like discounts/refunds)
        if (column.displayInParentheses) {
          // Format as positive value, then wrap in parentheses
          const absValue = Math.abs(numValue);
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(absValue);
          return `(${formatted})`;
        }

        // Format negative values with parentheses instead of minus sign
        if (numValue < 0) {
          const absValue = Math.abs(numValue);
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(absValue);
          return `(${formatted})`;
        }

        // Standard formatting for positive values
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);

      case 'percentage': {
        const pctValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(pctValue)) return value;
        const precision = column.precision ?? 1; // Default to 1 decimal place

        // Format negative values with parentheses instead of minus sign
        if (pctValue < 0) {
          const absValue = Math.abs(pctValue);
          return `(${absValue.toFixed(precision)}%)`;
        }

        return `${pctValue.toFixed(precision)}%`;
      }

      case 'number': {
        const numberValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numberValue)) return value;

        const precision = column.precision !== undefined ? column.precision : undefined;

        // Format negative values with parentheses instead of minus sign
        if (numberValue < 0) {
          const absValue = Math.abs(numberValue);
          if (precision !== undefined) {
            return `(${absValue.toFixed(precision)})`;
          }
          return `(${absValue.toLocaleString('en-US')})`;
        }

        if (precision !== undefined) {
          return numberValue.toFixed(precision);
        }
        return numberValue.toLocaleString('en-US');
      }

      case 'date':
        if (value instanceof Date) {
          return value.toLocaleDateString();
        }
        // Handle ISO date strings
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return new Date(value).toLocaleDateString();
        }
        return value;

      default:
        // Special handling for order type values in order-type-detail-table
        if (column.field === 'order_type' || column.id === 'location_order_type') {
          if (typeof value === 'string') {
            // Handle the specific case of DINEIN -> DINE-IN
            if (value === 'DINEIN') {
              return 'DINE-IN';
            }
          }
        }
        return value;
    }
  };

  // Apply conditional formatting
  const getCellStyle = (value: any, column: TableColumn) => {
    if (!column.conditionalFormatting) return {};

    for (const format of column.conditionalFormatting) {
      let conditionMet = false;

      switch (format.condition) {
        case 'negative':
          conditionMet = typeof value === 'number' && value < 0;
          break;
        case 'positive':
          conditionMet = typeof value === 'number' && value > 0;
          break;
        case 'greater':
          conditionMet = typeof value === 'number' && format.value != null && value > format.value;
          break;
        case 'less':
          conditionMet = typeof value === 'number' && format.value != null && value < format.value;
          break;
        case 'equal':
          conditionMet = value === format.value;
          break;
        case 'custom':
          if (format.customCondition) {
            conditionMet = format.customCondition(value, data);
          }
          break;
      }

      if (conditionMet) {
        return format.style;
      }
    }

    return {};
  };

  // Render table row
  const renderRow = (row: AnalyticsDataPoint, index: number) => {
    const level = row.level || 0;
    const hasChildren = row.hasChildren || false;
    const nodeKey = row.id || row.location || '';
    const isExpanded = expandedNodes.has(nodeKey);

    // Row rendering logic
    const isParentExpanded = hasChildren && isExpanded;

    return (
      <React.Fragment key={row.id || index}>
        <TableRow
          sx={{
            ...(isParentExpanded && {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            }),
          }}
        >
          {filteredColumns.map((column, colIndex) => {
            // Check if this is a divider column
            const isDivider = column.dataType === 'divider' || column.type === 'divider';

            // Render divider column
            if (isDivider) {
              return (
                <DividerCell
                  key={column.id}
                  sx={{
                    width: column.width || 4,
                    height: '64px',
                  }}
                />
              );
            }

            const value = row[column.field];
            const cellStyle = getCellStyle(value, column);
            const formattedValue = formatCellValue(value, column);

            // Column rendering logic

            // First column with expand/collapse functionality
            if (colIndex === 0 && column.expandIcon && config.hierarchical) {
              // Use dynamic display value for hierarchical tables
              const hierarchicalValue = row._displayValue || value;
              const hierarchicalFormatted = formatCellValue(hierarchicalValue, column);
              const columnAlign = getColumnAlignment(column);

              // Using hierarchical display value for proper level representation

              return (
                <ExpandableCell
                  key={column.id}
                  level={level}
                  sx={{
                    width: column.width,
                    position: 'sticky',
                    left: 0,
                    zIndex: 99,
                    backgroundColor: '#ffffff',
                    boxShadow: '2px 0px 4px rgba(0, 0, 0, 0.08)',
                    justifyContent: columnAlign === 'right' ? 'flex-end' : columnAlign === 'center' ? 'center' : 'flex-start',
                    ...cellStyle,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleToggleExpand(row)}
                    disabled={!hasChildren}
                    sx={{
                      visibility: hasChildren ? 'visible' : 'hidden',
                      padding: '4px',
                      marginLeft: '-4px',
                      marginRight: '4px',
                    }}
                  >
                    {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </IconButton>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: hasChildren ? 700 : (level === 0 ? 500 : 400),
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#18181b',
                      flex: 1,
                      textAlign: columnAlign,
                    }}
                  >
                    {hierarchicalFormatted}
                  </Typography>
                </ExpandableCell>
              );
            }


            // Regular cells
            const deviationResult = calculateDeviation ? calculateDeviation(row, column, value) : null;

            // Handle both old number format and new DeviationResult format
            const isDeviationObject = deviationResult !== null && typeof deviationResult === 'object' && 'deltaPercent' in deviationResult;
            const deltaPercent = isDeviationObject ? (deviationResult as any).deltaPercent : deviationResult as number | null;
            const deltaAbsolute = isDeviationObject ? (deviationResult as any).deltaAbsolute : null;

            const getDeviationStyle = (dev: number | null) => {
              if (!dev) return {};

              // Use absolute value for threshold checking (works for both positive and negative)
              const absDev = Math.abs(dev);

              // Updated thresholds: 0-15% = white, 15-25%, 26-50%, 51-75%, 76-100%, >100%
              if (absDev > 100) {
                // Greater than 100% - completely orange with white text
                return {
                  backgroundColor: '#f97316',
                  color: '#ffffff',
                };
              } else if (absDev >= 76 && absDev <= 100) {
                // 76-100%
                return {
                  backgroundColor: '#fff7ed',
                  color: '#18181b',
                };
              } else if (absDev >= 51 && absDev < 76) {
                // 51-75%
                return {
                  backgroundColor: '#ffedd5',
                  color: '#18181b',
                };
              } else if (absDev >= 26 && absDev < 51) {
                // 26-50%
                return {
                  backgroundColor: '#fed7aa',
                  color: '#18181b',
                };
              } else if (absDev >= 15 && absDev < 26) {
                // 15-25%
                return {
                  backgroundColor: '#fdba74',
                  color: '#18181b',
                };
              }
              // 0-15%: No highlighting (white background)
              return {};
            };

            // Always use percentage for heat map coloring (per requirements)
            const deviationStyle = getDeviationStyle(deltaPercent);
            const isFullOrange = deltaPercent !== null && Math.abs(deltaPercent) > 100;
            const columnAlign = getColumnAlignment(column);
            const isFirstColumn = colIndex === 0;

            // Format absolute delta based on column dataType (matches formatCellValue)
            const formatAbsoluteDelta = (val: number, col: TableColumn): string => {
              const absVal = Math.abs(val);
              const isNegative = val < 0;
              const wrap = (s: string) => (isNegative ? `(${s})` : s);
              switch (col.dataType) {
                case 'currency':
                  const currencyFormatted = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(absVal);
                  return wrap(currencyFormatted);
                case 'percentage': {
                  const precision = col.precision ?? 1;
                  const pctFormatted = `${absVal.toFixed(precision)}%`;
                  return wrap(pctFormatted);
                }
                case 'number': {
                  const precision = col.precision !== undefined ? col.precision : undefined;
                  const numFormatted = precision !== undefined
                    ? absVal.toFixed(precision)
                    : absVal.toLocaleString('en-US');
                  return wrap(numFormatted);
                }
                default:
                  return wrap(absVal.toLocaleString('en-US'));
              }
            };

            // Format variance values for tooltip (shows both Δ (absolute) and Δ%)
            const formatVarianceTooltip = () => {
              if (deltaPercent === null) return null;

              const deltaPercentFormatted = deltaPercent < 0
                ? `(${Math.abs(deltaPercent).toFixed(1)}%)`
                : `+${deltaPercent.toFixed(1)}%`;

              let deltaAbsoluteFormatted = '';
              if (deltaAbsolute !== null) {
                deltaAbsoluteFormatted = deltaAbsolute < 0
                  ? formatAbsoluteDelta(deltaAbsolute, column)
                  : `+${formatAbsoluteDelta(deltaAbsolute, column)}`;
              }

              return (
                <Box sx={{ p: 0.5 }}>
                  <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                    Variance Details
                  </Box>
                  {deltaAbsoluteFormatted && (
                    <Box sx={{ fontSize: '0.8125rem', mb: 0.25 }}>
                      <strong>Δ (abs):</strong> {deltaAbsoluteFormatted}
                    </Box>
                  )}
                  <Box sx={{ fontSize: '0.8125rem' }}>
                    <strong>Δ%:</strong> {deltaPercentFormatted}
                  </Box>
                  <Box sx={{ fontSize: '0.75rem', color: '#71717a', mt: 0.5, fontStyle: 'italic' }}>
                    Heat map color based on Δ%
                  </Box>
                </Box>
              );
            };

            const varianceTooltip = formatVarianceTooltip();
            const cellContent = (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: columnAlign === 'right' ? 'flex-end' : columnAlign === 'center' ? 'center' : 'flex-start',
                gap: 0.25,
                width: '100%',
              }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: hasChildren ? 700 : 400,
                    fontSize: '14px',
                    textAlign: columnAlign,
                  }}
                >
                  {formattedValue}
                </Typography>
                {deltaPercent !== null && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '12px',
                      fontWeight: 500,
                      color: isFullOrange ? '#ffffff' : '#f97316',
                    }}
                  >
                    {(() => {
                      // Display based on varianceDisplayMode
                      if (varianceDisplayMode === 'absolute' && deltaAbsolute !== null) {
                        // Display Δ (absolute change) - formatted per column dataType
                        return formatAbsoluteDelta(deltaAbsolute, column);
                      } else {
                        // Display Δ% (percentage change) - default
                        return deltaPercent < 0
                          ? `(${Math.abs(deltaPercent).toFixed(1)}%)`
                          : `${deltaPercent.toFixed(1)}%`;
                      }
                    })()}
                  </Typography>
                )}
              </Box>
            );

            return (
              <StyledTableCell
                key={column.id}
                level={level}
                sx={{
                  width: column.width,
                  textAlign: columnAlign,
                  ...(isFirstColumn && {
                    position: 'sticky',
                    left: 0,
                    zIndex: 99,
                    backgroundColor: '#ffffff',
                    boxShadow: '2px 0px 4px rgba(0, 0, 0, 0.08)',
                  }),
                  ...cellStyle,
                  ...deviationStyle,
                  position: isFirstColumn ? 'sticky' : 'relative',
                }}
              >
                {varianceTooltip ? (
                  <Tooltip
                    title={varianceTooltip}
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#ffffff',
                          color: '#18181b',
                          border: '1px solid #e4e4e7',
                          borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          fontFamily: "'Inter', sans-serif",
                          maxWidth: '200px',
                        }
                      },
                      arrow: {
                        sx: {
                          color: '#ffffff',
                          '&::before': {
                            border: '1px solid #e4e4e7',
                          }
                        }
                      }
                    }}
                  >
                    <Box sx={{ width: '100%', cursor: 'help' }}>
                      {cellContent}
                    </Box>
                  </Tooltip>
                ) : (
                  cellContent
                )}
              </StyledTableCell>
            );
          })}
        </TableRow>
      </React.Fragment>
    );
  };


  return (
    <Box>
      {renderColumnFilter()}
      <StyledTableContainer ref={containerRef}>
        <Box
          sx={{
            zoom: tableScale,
            width: '100%',
            display: 'block',
          }}
        >
          <Table
            ref={tableRef}
            size={config.compact ? 'small' : 'medium'}
            className={className}
            sx={{ width: '100%', minWidth: 650 }}
          >
            <StyledTableHead>
              {/* Banner row for partitions - only for order-type-detail-table */}
              {config?.columns && config.columns.some((col: any) => col.id === 'sales_divider') && (
                <TableRow>
                  {(() => {
                    // Find the divider column index in filtered columns
                    const dividerIndex = filteredColumns.findIndex(col =>
                      col.id === 'sales_divider' || (col.dataType === 'divider' || col.type === 'divider')
                    );

                    const cells: React.ReactNode[] = [];

                    if (dividerIndex >= 0) {
                      // Net Sales partition: columns before divider
                      const netSalesSpan = dividerIndex;

                      // Net Sales banner (neon green)
                      if (netSalesSpan > 0) {
                        cells.push(
                          <TableCell
                            key="net-sales-banner"
                            colSpan={netSalesSpan}
                            sx={{
                              backgroundColor: '#39ff14 !important',
                              padding: '0 !important',
                              borderBottom: 'none !important',
                              height: '3px !important',
                              position: 'sticky',
                              top: 0,
                              zIndex: 102,
                              '&.MuiTableCell-root': {
                                backgroundColor: '#39ff14 !important',
                                padding: '0 !important',
                                borderBottom: 'none !important',
                                height: '3px !important',
                              },
                            }}
                          />
                        );
                      }

                      // Divider cell
                      cells.push(
                        <TableCell
                          key="divider-banner"
                          sx={{
                            padding: '0 !important',
                            borderBottom: 'none !important',
                            height: '3px !important',
                            backgroundColor: '#ffffff !important',
                            width: filteredColumns[dividerIndex]?.width || 4,
                            '&.MuiTableCell-root': {
                              backgroundColor: '#ffffff !important',
                              padding: '0 !important',
                              borderBottom: 'none !important',
                              height: '3px !important',
                            },
                          }}
                        />
                      );

                      // Payments Collected partition: columns after divider
                      const paymentsCollectedSpan = filteredColumns.length - dividerIndex - 1;

                      // Payments Collected banner (neon blue)
                      if (paymentsCollectedSpan > 0) {
                        cells.push(
                          <TableCell
                            key="payments-collected-banner"
                            colSpan={paymentsCollectedSpan}
                            sx={{
                              backgroundColor: '#00ffff !important',
                              padding: '0 !important',
                              borderBottom: 'none !important',
                              height: '3px !important',
                              position: 'sticky',
                              top: 0,
                              zIndex: 102,
                              '&.MuiTableCell-root': {
                                backgroundColor: '#00ffff !important',
                                padding: '0 !important',
                                borderBottom: 'none !important',
                                height: '3px !important',
                              },
                            }}
                          />
                        );
                      }
                    } else {
                      // Fallback: if no divider found, assume first 4 columns are Net Sales
                      const netSalesSpan = Math.min(4, filteredColumns.length);
                      const paymentsCollectedSpan = filteredColumns.length - netSalesSpan;

                      if (netSalesSpan > 0) {
                        cells.push(
                          <TableCell
                            key="net-sales-banner"
                            colSpan={netSalesSpan}
                            sx={{
                              backgroundColor: '#39ff14 !important',
                              padding: '0 !important',
                              borderBottom: 'none !important',
                              height: '3px !important',
                              position: 'sticky',
                              top: 0,
                              zIndex: 102,
                              '&.MuiTableCell-root': {
                                backgroundColor: '#39ff14 !important',
                                padding: '0 !important',
                                borderBottom: 'none !important',
                                height: '3px !important',
                              },
                            }}
                          />
                        );
                      }

                      if (paymentsCollectedSpan > 0) {
                        cells.push(
                          <TableCell
                            key="payments-collected-banner"
                            colSpan={paymentsCollectedSpan}
                            sx={{
                              backgroundColor: '#00ffff !important',
                              padding: '0 !important',
                              borderBottom: 'none !important',
                              height: '3px !important',
                              position: 'sticky',
                              top: 0,
                              zIndex: 102,
                              '&.MuiTableCell-root': {
                                backgroundColor: '#00ffff !important',
                                padding: '0 !important',
                                borderBottom: 'none !important',
                                height: '3px !important',
                              },
                            }}
                          />
                        );
                      }
                    }

                    return cells;
                  })()}
                </TableRow>
              )}
              <TableRow>
                {filteredColumns.map((column, colIndex) => {
                  const isDivider = column.dataType === 'divider' || column.type === 'divider';
                  const prevColumn = colIndex > 0 ? filteredColumns[colIndex - 1] : null;
                  const isNextToDivider = prevColumn && (prevColumn.dataType === 'divider' || prevColumn.type === 'divider');
                  const dividerDescription = isNextToDivider ? prevColumn?.description : null;

                  // Render divider column
                  if (isDivider) {
                    return (
                      <DividerHeaderCell
                        key={column.id}
                        sx={{
                          width: column.width || 4,
                          position: 'relative',
                        }}
                      />
                    );
                  }

                  const columnAlign = getColumnAlignment(column);
                  const isFirstColumn = colIndex === 0;
                  return (
                    <TableCell
                      key={column.id}
                      align={columnAlign}
                      sx={{
                        width: column.width,
                        backgroundColor: '#ffffff !important',
                        color: '#71717a',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        borderBottom: '1px solid #e4e4e7',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        height: '56px',
                        verticalAlign: 'middle',
                        position: 'sticky',
                        top: config?.columns && config.columns.some((col: any) => col.id === 'sales_divider') ? '3px' : 0,
                        left: isFirstColumn ? 0 : 'auto',
                        zIndex: isFirstColumn ? 101 : 100,
                        boxShadow: isFirstColumn
                          ? '2px 2px 4px rgba(0, 0, 0, 0.08)'
                          : '0px 2px 4px rgba(0, 0, 0, 0.08)',
                        textAlign: columnAlign,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent={columnAlign === 'right' ? 'flex-end' : columnAlign === 'center' ? 'center' : 'flex-start'} gap={0.2} position="relative">
                        {config.sortable && column.sortable !== false ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {column.revealsColumns && column.revealsColumns.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newExpanded = new Set(expandedColumns);
                                  const willBeExpanded = !expandedColumns.has(column.id);
                                  if (willBeExpanded) {
                                    newExpanded.add(column.id);
                                  } else {
                                    newExpanded.delete(column.id);
                                  }
                                  setExpandedColumns(newExpanded);
                                  if ((column.id === 'credit_cards_total' || column.id === 'credit_card_payments') && onCreditCardDetailChange) {
                                    onCreditCardDetailChange(willBeExpanded);
                                  } else if (column.id === 'third_party_delivery' && onThirdPartyDetailChange) {
                                    onThirdPartyDetailChange(willBeExpanded);
                                  }
                                }}
                                sx={{
                                  padding: '2px',
                                  marginRight: '-4px',
                                  color: '#71717a',
                                  '&:hover': {
                                    backgroundColor: '#f4f4f5',
                                    color: '#18181b',
                                  },
                                }}
                              >
                                {expandedColumns.has(column.id) ? (
                                  <ChevronDownIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                            <TableSortLabel
                              active={sortConfig?.field === column.field}
                              direction={sortConfig?.field === column.field ? sortConfig.direction : 'asc'}
                              onClick={() => handleSort(column.field)}
                              sx={{
                                color: '#71717a',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                                '&.Mui-active': {
                                  color: '#18181b',
                                },
                                '& .MuiTableSortLabel-icon': {
                                  color: '#71717a !important',
                                },
                                '&:hover': {
                                  color: '#18181b',
                                },
                              }}
                            >
                              {column.header}
                            </TableSortLabel>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {column.revealsColumns && column.revealsColumns.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newExpanded = new Set(expandedColumns);
                                  const willBeExpanded = !expandedColumns.has(column.id);
                                  if (willBeExpanded) {
                                    newExpanded.add(column.id);
                                  } else {
                                    newExpanded.delete(column.id);
                                  }
                                  setExpandedColumns(newExpanded);
                                  if ((column.id === 'credit_cards_total' || column.id === 'credit_card_payments') && onCreditCardDetailChange) {
                                    onCreditCardDetailChange(willBeExpanded);
                                  } else if (column.id === 'third_party_delivery' && onThirdPartyDetailChange) {
                                    onThirdPartyDetailChange(willBeExpanded);
                                  }
                                }}
                                sx={{
                                  padding: '2px',
                                  marginRight: '-4px',
                                  color: '#71717a',
                                  '&:hover': {
                                    backgroundColor: '#f4f4f5',
                                    color: '#18181b',
                                  },
                                }}
                              >
                                {expandedColumns.has(column.id) ? (
                                  <ChevronDownIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                            <Typography
                              sx={{
                                color: '#71717a',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                              }}
                            >
                              {column.header}
                            </Typography>
                          </Box>
                        )}
                        {hasColumnTooltip(column) && (
                          <Tooltip title={getColumnTooltip(column)} placement="top" arrow>
                            <InfoIcon
                              sx={{
                                fontSize: 16,
                                color: '#71717a',
                                cursor: 'help',
                                '&:hover': { color: '#18181b' }
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {sortedData.map((row, index) => renderRow(row, index))}
            </TableBody>
          </Table>
        </Box>
      </StyledTableContainer>
    </Box>
  );
});

AnalyticsTable.displayName = 'AnalyticsTable'; 